import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';
import { GuestRoute, LogoutRoute, ProtectedRoute, RequireAccess } from './guards';
import { paths } from './paths';
import { isLocationLevel } from '@/features/masters/locations.config';
import { MASTER_SCREENS, SCREENS } from './screens';

/** Code-split a page. Each screen ships as its own chunk. */
function lazyPage<P extends object>(load: () => Promise<ComponentType<P>>) {
  return lazy(async () => ({ default: await load() }));
}

const AnimalMasterPage = lazyPage(() =>
  import('@/features/masters/pages/AnimalMasterPage').then((m) => m.AnimalMasterPage),
);
const LocationMasterPage = lazyPage(() =>
  import('@/features/masters/pages/LocationMasterPage').then((m) => m.LocationMasterPage),
);

const SCREEN_PAGES: Record<keyof typeof SCREENS, LazyExoticComponent<ComponentType>> = {
  dashboard: lazyPage(() =>
    import('@/features/dashboard/pages/DashboardPage').then((m) => m.DashboardPage),
  ),
  adminUsers: lazyPage(() =>
    import('@/features/users/pages/AdminUsersPage').then((m) => m.AdminUsersPage),
  ),
  clientUsers: lazyPage(() =>
    import('@/features/clients/pages/ClientUsersPage').then((m) => m.ClientUsersPage),
  ),
  loginLogs: lazyPage(() =>
    import('@/features/login-logs/pages/LoginLogsPage').then((m) => m.LoginLogsPage),
  ),
  mlLogs: lazyPage(() => import('@/features/ml-logs/pages/MlLogsPage').then((m) => m.MlLogsPage)),
  faqs: lazyPage(() => import('@/features/faqs/pages/FaqsPage').then((m) => m.FaqsPage)),
  farmers: lazyPage(() =>
    import('@/features/farmers/pages/FarmersPage').then((m) => m.FarmersPage),
  ),
  notifications: lazyPage(() =>
    import('@/features/notifications/pages/NotificationsPage').then((m) => m.NotificationsPage),
  ),
  grievances: lazyPage(() =>
    import('@/features/grievances/pages/GrievancesPage').then((m) => m.GrievancesPage),
  ),
  grievanceArchive: lazyPage(() =>
    import('@/features/grievances/pages/GrievancesPage').then((m) => m.ArchivedGrievancesPage),
  ),
  animals: lazyPage(() =>
    import('@/features/animals/pages/AnimalsPage').then((m) => m.AnimalsPage),
  ),
  administrators: lazyPage(() =>
    import('@/features/users/pages/AdministratorsPage').then((m) => m.AdministratorsPage),
  ),
  agents: lazyPage(() => import('@/features/users/pages/AgentsPage').then((m) => m.AgentsPage)),
};

const screenRoutes: RouteObject[] = (Object.keys(SCREENS) as (keyof typeof SCREENS)[]).map(
  (key) => {
    const Page = SCREEN_PAGES[key];
    return {
      path: SCREENS[key].path,
      element: (
        <RequireAccess rule={SCREENS[key].access}>
          <Page />
        </RequireAccess>
      ),
    };
  },
);

const FarmerDetailPage = lazyPage(() =>
  import('@/features/farmers/pages/FarmerDetailPage').then((m) => m.FarmerDetailPage),
);
const AnimalDetailPage = lazyPage(() =>
  import('@/features/animals/pages/AnimalDetailPage').then((m) => m.AnimalDetailPage),
);

const FieldAdminDetailPage = lazyPage(() =>
  import('@/features/users/pages/FieldAdminDetailPage').then((m) => m.FieldAdminDetailPage),
);
const AgentDetailPage = lazyPage(() =>
  import('@/features/users/pages/AgentDetailPage').then((m) => m.AgentDetailPage),
);
const AgentActivitiesPage = lazyPage(() =>
  import('@/features/users/pages/AgentActivitiesPage').then((m) => m.AgentActivitiesPage),
);

/** Pages reached from a list, not the sidebar; they share the list's access rule. */
const GrievanceDetailPage = lazyPage(() =>
  import('@/features/grievances/pages/GrievanceDetailPage').then((m) => m.GrievanceDetailPage),
);
const ArchivedGrievanceDetailPage = lazyPage(() =>
  import('@/features/grievances/pages/GrievanceDetailPage').then(
    (m) => m.ArchivedGrievanceDetailPage,
  ),
);

const MlLogDetailPage = lazyPage(() =>
  import('@/features/ml-logs/pages/MlLogDetailPage').then((m) => m.MlLogDetailPage),
);

const detailRoutes: RouteObject[] = [
  {
    path: `${paths.mlLogs}/:id`,
    element: (
      <RequireAccess rule={SCREENS.mlLogs.access}>
        <MlLogDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.administrators}/:id`,
    element: (
      <RequireAccess rule={SCREENS.administrators.access}>
        <FieldAdminDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.agents}/:id`,
    element: (
      <RequireAccess rule={SCREENS.agents.access}>
        <AgentDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.agents}/:id/activities`,
    element: (
      <RequireAccess rule={SCREENS.agents.access}>
        <AgentActivitiesPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.farmers}/:id`,
    element: (
      <RequireAccess rule={SCREENS.farmers.access}>
        <FarmerDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.animals}/:id`,
    element: (
      <RequireAccess rule={SCREENS.animals.access}>
        <AnimalDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.grievances}/:id`,
    element: (
      <RequireAccess rule={SCREENS.grievances.access}>
        <GrievanceDetailPage />
      </RequireAccess>
    ),
  },
  {
    path: `${paths.grievanceArchive}/:id`,
    element: (
      <RequireAccess rule={SCREENS.grievanceArchive.access}>
        <ArchivedGrievanceDetailPage />
      </RequireAccess>
    ),
  },
];

const masterRoutes: RouteObject[] = MASTER_SCREENS.map((screen) => ({
  path: screen.path,
  element: (
    <RequireAccess rule={screen.access}>
      {isLocationLevel(screen.key) ? (
        <LocationMasterPage key={screen.key} level={screen.key} />
      ) : (
        <AnimalMasterPage key={screen.key} masterKey={screen.key} />
      )}
    </RequireAccess>
  ),
}));

/**
 * The route tree. `App` mounts it in a browser router; tests mount it in a memory router.
 * Access rules come from `screens.ts`, the same table the sidebar reads.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to={paths.dashboard} replace /> },
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: paths.login, element: <LoginPage /> },
              { path: paths.forgotPassword, element: <ForgotPasswordPage /> },
            ],
          },
        ],
      },
      { path: paths.logout, element: <LogoutRoute /> },
      {
        element: <ProtectedRoute />,
        children: [
          { element: <AppLayout />, children: [...screenRoutes, ...detailRoutes, ...masterRoutes] },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
